import { useEffect, useRef, useState } from 'react';
import {
  Upload, FileText, FileImage, FileAudio, File as FileIcon,
  Trash2, Loader2, Sparkles, Check, X, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import { SplitViewTrigger } from '../components/SplitViewLayout';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCEPTED_EXTS = '.txt,.md,.pdf,.docx,.jpg,.jpeg,.png,.heic,.heif,.webp,.mp3,.m4a,.wav';
const ACCEPTED_DESCRIPTION = '.txt, .md, .pdf, .docx, images, voice memos';
const MAX_FILE_MB = 25;

const DEPTHS = [
  { value: 'surface',  label: 'Surface scan', desc: 'Quick read — surface obvious patterns' },
  { value: 'standard', label: 'Standard',     desc: 'Carry a careful read with concrete suggestions' },
  { value: 'deep',     label: 'Deep dive',    desc: 'Story-editor pass with quotes and next moves' },
];

function fileTypeIcon(mime = '') {
  if (mime.startsWith('image/')) return FileImage;
  if (mime.startsWith('audio/')) return FileAudio;
  if (mime === 'application/pdf' || mime.includes('word')) return FileText;
  if (mime.startsWith('text/')) return FileText;
  return FileIcon;
}

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function NotesTab({ project, navigate, splitProps }) {
  const projectId = project?.project_id;
  const isPlot = project?.project_type === 'plot';
  const fileInputRef = useRef(null);

  const [notes, setNotes] = useState([]);
  const [generalNotes, setGeneralNotes] = useState('');
  const [generalSavedTimer, setGeneralSavedTimer] = useState(null);
  const [generalSaving, setGeneralSaving] = useState(false);
  const [uploading, setUploading] = useState({}); // { tempId: { name, progress } }
  const [dragActive, setDragActive] = useState(false);

  const [analysisDepth, setAnalysisDepth] = useState('standard');
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({}); // { idx: 'accepted' | 'rejected' }

  // ── Load notes on mount ────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${API}/projects/${projectId}/notes`, { credentials: 'include' });
        if (!r.ok) throw new Error('load failed');
        const data = await r.json();
        if (cancelled) return;
        setNotes(data.notes || []);
        setGeneralNotes(data.general_notes || '');
        // Surface the most recent analysis if it's cached on the latest note.
        const latest = (data.notes || []).slice().reverse().find((n) => n.ai_analysis);
        if (latest?.ai_analysis) setAnalysis(latest.ai_analysis);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load notes');
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // ── General notes auto-save (debounced 800ms) ─────────────────────────
  useEffect(() => {
    if (!projectId) return;
    if (generalSavedTimer) clearTimeout(generalSavedTimer);
    const t = setTimeout(async () => {
      try {
        setGeneralSaving(true);
        await authFetch(`${API}/projects/${projectId}/general-notes`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ general_notes: generalNotes }),
        });
      } catch (e) {
        console.error(e);
      } finally {
        setGeneralSaving(false);
      }
    }, 800);
    setGeneralSavedTimer(t);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalNotes]);

  // ── Upload pipeline ────────────────────────────────────────────────────
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    for (const f of files) {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_FILE_MB} MB — split it first`);
        continue;
      }
      await uploadOne(f);
    }
  };

  const uploadOne = async (file) => {
    const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setUploading((u) => ({ ...u, [tempId]: { name: file.name, progress: 0 } }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authFetch(`${API}/projects/${projectId}/notes`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'upload failed');
      }
      const note = await r.json();
      setNotes((prev) => [...prev, note]);
      const hasText = (note.extracted_text || '').trim().length > 0;
      toast.success(
        hasText
          ? `${note.file_name} uploaded — ${note.extracted_text.length.toLocaleString()} chars extracted`
          : `${note.file_name} uploaded`
      );
    } catch (e) {
      console.error(e);
      toast.error(`Upload failed: ${file.name}`);
    } finally {
      setUploading((u) => {
        const next = { ...u };
        delete next[tempId];
        return next;
      });
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm(`Delete "${note.file_name}"? This removes both the file and the extracted text.`)) return;
    try {
      const r = await authFetch(`${API}/projects/${projectId}/notes/${note.note_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error('delete failed');
      setNotes((prev) => prev.filter((n) => n.note_id !== note.note_id));
      toast.success('Note deleted');
    } catch (e) {
      console.error(e);
      toast.error('Delete failed');
    }
  };

  // ── AI analysis ────────────────────────────────────────────────────────
  const runAnalysis = async () => {
    if (!notes.length && !generalNotes.trim()) {
      toast.error('Upload a file or write some general notes first');
      return;
    }
    setAnalysisLoading(true);
    setAcceptedSuggestions({});
    try {
      const r = await authFetch(`${API}/ai/notes-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ project_id: projectId, depth: analysisDepth }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'analysis failed');
      }
      const data = await r.json();
      setAnalysis(data.analysis);
      toast.success('Analysis complete');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Analysis failed');
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Writes ONE setup field with fresh-fetched optimistic-concurrency state.
  // Sending only the changed field (the endpoint $sets only what's present)
  // means a stale workspace `project` can no longer silently revert the
  // writer's other setup fields, and base_rev threading means a genuinely
  // concurrent edit 409s instead of being overwritten. Returns false when
  // the write did not land.
  const saveSetupField = async (field, value) => {
    const fresh = await authFetch(`${API}/projects/${projectId}`, { credentials: 'include' });
    if (!fresh.ok) {
      toast.error('Could not load the project — try again');
      return false;
    }
    const doc = await fresh.json();
    const res = await authFetch(`${API}/projects/${projectId}/setup`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ [field]: value, base_rev: doc.content_rev ?? null }),
    });
    if (res.status === 409) {
      toast.error('This project was changed elsewhere — try again');
      return false;
    }
    if (!res.ok) {
      toast.error('Update failed — try again');
      return false;
    }
    return true;
  };

  // ── Suggestion accept ─────────────────────────────────────────────────
  const acceptSuggestion = async (sug, idx) => {
    try {
      const payload = sug.payload || {};
      const t = sug.type;

      if (t === 'add_general_note') {
        const addition = (payload.text || '').trim();
        if (!addition) throw new Error('empty payload');
        const sep = generalNotes.trim() ? '\n\n' : '';
        const next = generalNotes + sep + addition;
        setGeneralNotes(next);
        // PATCH directly so the user sees it persisted immediately
        await authFetch(`${API}/projects/${projectId}/general-notes`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ general_notes: next }),
        });
        toast.success('Added to general notes');
      } else if (t === 'update_logline' && isPlot) {
        if (!(await saveSetupField('logline', payload.logline || ''))) return;
        toast.success('Logline updated — refresh the Plot tab to see it');
      } else if (t === 'update_synopsis' && isPlot) {
        if (!(await saveSetupField('synopsis', payload.synopsis || ''))) return;
        toast.success('Synopsis updated');
      } else if (t === 'create_character') {
        // Hand off to CreateProject with the bio prefilled. We use sessionStorage
        // because URL params don't survive long biography text cleanly.
        const draft = {
          title: payload.name || 'Untitled character',
          name: payload.name || '',
          biography: payload.biography || '',
          age: payload.age || '',
          gender: payload.gender || '',
          ethnicity: payload.ethnicity || '',
        };
        sessionStorage.setItem('pangaji_character_draft', JSON.stringify(draft));
        navigate(`/create-project?type=character&workspace=${projectId}&from=notes`);
        toast.success('Opening Character Lab with draft prefilled');
      } else if (t === 'add_world_item') {
        // Same handoff pattern — WorldBuilding picks up the draft on mount.
        const draft = {
          category: payload.category || 'locations',
          name: payload.name || '',
          description: payload.description || '',
          ...payload, // pass through any extra fields the AI surfaced
        };
        sessionStorage.setItem('pangaji_world_draft', JSON.stringify(draft));
        navigate(`/world-building?from=notes&project_id=${projectId}`);
        toast.success('Opening World Building with draft prefilled');
      } else if (t === 'update_beat' && isPlot) {
        const beatKey = payload.beat_key;
        if (!beatKey) throw new Error('missing beat_key');
        // Open the beat editor with prefill — Plot levels accept ?prefill in URL.
        sessionStorage.setItem('pangaji_beat_draft', JSON.stringify({
          beat_key: beatKey, value: payload.value || '',
        }));
        navigate(`/plot/${projectId}/setup?from=notes`);
        toast.success(`Beat "${beatKey}" draft saved — open Plot Forge to apply`);
      } else {
        // Unknown / unsupported suggestion type — just show what would have happened.
        toast.info(`Suggestion noted (${t}). No automatic handler yet.`);
      }
      setAcceptedSuggestions((s) => ({ ...s, [idx]: 'accepted' }));
    } catch (e) {
      console.error(e);
      toast.error(`Couldn't apply suggestion: ${e.message || 'unknown'}`);
    }
  };

  const rejectSuggestion = (idx) => {
    setAcceptedSuggestions((s) => ({ ...s, [idx]: 'rejected' }));
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-mono text-3xl font-semibold tracking-tight mb-2">Notes & References</h2>
          <p className="text-muted-foreground">
            Upload research, scripts, voice memos, and reference photos. Pangaji extracts the text,
            cross-references it with your project, and surfaces what's missing.
          </p>
        </div>
        {splitProps && <SplitViewTrigger {...splitProps} />}
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed cursor-pointer transition-all p-10 text-center ${
          dragActive
            ? 'border-primary bg-primary/5'
            : 'border-border/40 bg-card/50 hover:border-primary/50 hover:bg-card'
        }`}
        data-testid="notes-dropzone"
      >
        <Upload className="w-10 h-10 text-primary/70 mx-auto mb-4" />
        <p className="font-mono text-sm uppercase tracking-widest text-foreground mb-2">
          Drop files here or click to browse
        </p>
        <p className="text-xs font-mono text-muted-foreground">
          {ACCEPTED_DESCRIPTION} · up to {MAX_FILE_MB} MB each
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTS}
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* In-flight uploads */}
      {Object.entries(uploading).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploading).map(([id, u]) => (
            <div key={id} className="flex items-center gap-3 px-4 py-2 bg-card border border-border/40">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="font-mono text-sm flex-1 truncate">{u.name}</span>
              <span className="font-mono text-xs text-muted-foreground">extracting…</span>
            </div>
          ))}
        </div>
      )}

      {/* Note list */}
      {notes.length > 0 && (
        <div>
          <h3 className="font-mono text-sm uppercase tracking-widest text-muted-foreground mb-4">
            Uploaded notes ({notes.length})
          </h3>
          <div className="space-y-2">
            {notes.map((n) => {
              const Icon = fileTypeIcon(n.file_type);
              const hasText = (n.extracted_text || '').trim().length > 0;
              return (
                <details key={n.note_id} className="bg-card border border-border/40 group">
                  <summary className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-card/80 list-none">
                    <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm truncate">{n.file_name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                        {n.file_type || 'unknown'} · {formatBytes(n.file_size_bytes)}
                        {hasText && ` · ${n.extracted_text.length.toLocaleString()} chars`}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(n); }}
                      className="rounded-none"
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </summary>
                  <div className="px-4 py-3 border-t border-border/40">
                    {hasText ? (
                      <AutoExpandTextarea
                        value={n.extracted_text}
                        readOnly
                        className="rounded-none font-mono text-xs bg-background/40 border-input min-h-[80px]"
                      />
                    ) : (
                      <p className="text-xs text-muted-foreground italic">
                        No text was extracted from this file.
                      </p>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      )}

      {/* General notes scratchpad */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-mono text-sm uppercase tracking-widest text-muted-foreground">
            General notes
          </h3>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {generalSaving ? 'saving…' : 'auto-saves'}
          </span>
        </div>
        <AutoExpandTextarea
          value={generalNotes}
          onChange={(e) => setGeneralNotes(e.target.value)}
          placeholder="Free-form thoughts. Hunches. References. Anything that doesn't fit a beat or a character yet."
          className="rounded-none font-mono bg-background border-input min-h-[140px]"
          data-testid="general-notes-textarea"
        />
      </div>

      {/* AI Analysis trigger */}
      <div className="border p-6" style={{ borderColor: 'rgba(232,160,32,0.3)', background: 'rgba(232,160,32,0.04)' }}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Sparkles className="w-5 h-5" style={{ color: '#E8A020' }} />
              <h3 className="font-mono text-lg font-semibold tracking-tight">AI Analysis</h3>
            </div>
            <p className="text-xs font-mono text-muted-foreground">
              Reflection-agent pass: identifies elements, contradictions, open questions, and concrete next moves.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          {DEPTHS.map((d) => (
            <button
              key={d.value}
              onClick={() => setAnalysisDepth(d.value)}
              data-testid={`depth-${d.value}`}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition-all ${
                analysisDepth === d.value
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border/40 bg-background/50 text-muted-foreground hover:border-primary/50 hover:text-foreground'
              }`}
              title={d.desc}
            >
              {d.label}
            </button>
          ))}
        </div>

        <Button
          onClick={runAnalysis}
          disabled={analysisLoading}
          data-testid="run-analysis-btn"
          className="rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 disabled:opacity-50"
          style={{ background: '#E8A020', color: '#000' }}
        >
          {analysisLoading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</>
            : <><Sparkles className="w-4 h-4 mr-2" /> Analyze notes</>}
        </Button>
      </div>

      {/* Analysis result */}
      {analysis && (() => {
        const totalFindings =
          (analysis.elements?.length || 0) +
          (analysis.contradictions?.length || 0) +
          (analysis.questions?.length || 0) +
          (analysis.suggestions?.length || 0);

        // Empty-result fallback: show *something* so the user knows the
        // analysis ran but came back with no structured findings — usually
        // because Gemini returned a non-JSON response and the backend's
        // parser fell back to defaults. The raw_response field, if present,
        // lets us inspect what actually came back.
        if (totalFindings === 0) {
          return (
            <div className="border-t border-border/40 pt-8">
              <div
                className="border p-6 bg-card"
                style={{ borderColor: 'rgba(232,160,32,0.3)' }}
                data-testid="analysis-empty-state"
              >
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#E8A020' }} />
                  <div>
                    <h3 className="font-mono text-sm uppercase tracking-widest mb-2" style={{ color: '#E8A020' }}>
                      No structured findings
                    </h3>
                    <p className="text-sm text-muted-foreground font-mono leading-relaxed">
                      The analysis ran but returned no elements, contradictions, questions, or suggestions.
                      This usually means the AI response wasn't structured JSON. Try running again, or
                      switch depth (deeper passes are more reliable on short inputs).
                    </p>
                    {analysis.raw_response && (
                      <details className="mt-4">
                        <summary className="font-mono text-xs uppercase tracking-widest text-muted-foreground cursor-pointer">
                          Show raw AI response
                        </summary>
                        <pre className="mt-2 p-3 bg-background/40 border border-border/40 text-xs font-mono whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                          {analysis.raw_response}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return (
        <div className="space-y-6 border-t border-border/40 pt-8">
          {analysis.elements?.length > 0 && (
            <Section title="Elements identified" items={analysis.elements} accent="#E8A020" />
          )}
          {analysis.contradictions?.length > 0 && (
            <Section title="Contradictions" items={analysis.contradictions} accent="#c0392b" Icon={AlertTriangle} />
          )}
          {analysis.questions?.length > 0 && (
            <Section title="Open questions" items={analysis.questions} accent="#7a8fb3" />
          )}
          {analysis.suggestions?.length > 0 && (
            <div>
              <h3 className="font-mono text-sm uppercase tracking-widest mb-3" style={{ color: '#E8A020' }}>
                Suggested actions
              </h3>
              <div className="space-y-3">
                {analysis.suggestions.map((s, i) => {
                  const status = acceptedSuggestions[i];
                  return (
                    <div
                      key={i}
                      className={`bg-card border p-4 transition-all ${
                        status === 'accepted'
                          ? 'border-green-500/50 opacity-80'
                          : status === 'rejected'
                            ? 'border-border/30 opacity-50'
                            : 'border-border/40'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1">
                          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                            {s.type?.replace(/_/g, ' ')}
                          </div>
                          <div className="font-mono text-sm font-semibold mb-2">{s.label}</div>
                          {s.rationale && (
                            <div className="text-xs text-muted-foreground italic">{s.rationale}</div>
                          )}
                        </div>
                        {!status && (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => rejectSuggestion(i)}
                              className="rounded-none font-mono uppercase text-[10px] tracking-widest"
                            >
                              <X className="w-3 h-3 mr-1" /> Reject
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => acceptSuggestion(s, i)}
                              className="rounded-none font-mono uppercase text-[10px] tracking-widest bg-primary text-primary-foreground"
                            >
                              <Check className="w-3 h-3 mr-1" /> Accept
                            </Button>
                          </div>
                        )}
                        {status === 'accepted' && (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-green-500 flex-shrink-0">
                            <Check className="w-3 h-3 inline mr-1" /> Accepted
                          </span>
                        )}
                        {status === 'rejected' && (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex-shrink-0">
                            Rejected
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
}

function Section({ title, items, accent, Icon }) {
  return (
    <div>
      <h3
        className="font-mono text-sm uppercase tracking-widest mb-3 flex items-center gap-2"
        style={{ color: accent }}
      >
        {Icon && <Icon className="w-4 h-4" />}
        {title}
      </h3>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="bg-card border border-border/40 px-4 py-3 text-sm font-mono">
            {typeof it === 'string' ? it : JSON.stringify(it)}
          </li>
        ))}
      </ul>
    </div>
  );
}
